/**
 * EVM wallet detection & WalletConnect (EIP-1193).
 * References: Brave wallet-docs.brave.com; Rabby rabby.io/docs; OKX web3.okx.com dev docs;
 * Coinbase / Frame multi-injected `ethereum.providers`; WalletConnect v2.
 */

export function findInjected(match) {
  if (typeof window === 'undefined') {
    return null;
  }
  const eth = window.ethereum;
  if (!eth) {
    return null;
  }
  if (Array.isArray(eth.providers)) {
    return eth.providers.find(match) || null;
  }
  return match(eth) ? eth : null;
}

export const getMetaMaskProvider = () =>
  findInjected((p) => p.isMetaMask === true && !p.isBraveWallet);

export const getBraveProvider = () => findInjected((p) => Boolean(p.isBraveWallet));

export const getRabbyProvider = () => findInjected((p) => Boolean(p.isRabby));

export const getCoinbaseProvider = () => findInjected((p) => Boolean(p.isCoinbaseWallet));

export const getFrameProvider = () => findInjected((p) => Boolean(p.isFrame));

export const getOKXProvider = () =>
  typeof window !== 'undefined' ? window.okxwallet?.ethereum || null : null;

export function resolveInjectedProvider(providerId) {
  switch (providerId) {
    case 'metamask':
      return getMetaMaskProvider();
    case 'brave':
      return getBraveProvider();
    case 'rabby':
      return getRabbyProvider();
    case 'coinbase':
      return getCoinbaseProvider();
    case 'frame':
      return getFrameProvider();
    case 'okx':
      return getOKXProvider();
    default:
      return null;
  }
}

export function isInjectedAvailable(providerId) {
  return Boolean(resolveInjectedProvider(providerId));
}

/** WalletConnect v2 EIP-1193 provider + first account + chain hex. */
export async function connectWalletConnectEvm() {
  const projectId = (process.env.REACT_APP_WALLETCONNECT_PROJECT_ID || '').trim();
  if (!projectId) {
    throw new Error('WalletConnect needs REACT_APP_WALLETCONNECT_PROJECT_ID in dashboard env');
  }
  const { default: EthereumProvider } = await import('@walletconnect/ethereum-provider');
  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const provider = await EthereumProvider.init({
    projectId,
    chains: [1],
    optionalChains: [8453, 137, 42161, 56],
    showQrModal: true,
    metadata: {
      name: 'Account Hub',
      description: 'Wallet integrations',
      url: origin,
      icons: origin ? [`${origin}/logo192.png`] : [],
    },
  });
  const accounts = await provider.enable();
  const account = Array.isArray(accounts) && accounts.length > 0 ? accounts[0] : '';
  const chainHexRaw = await provider.request({ method: 'eth_chainId' });
  const chainHex = String(chainHexRaw || '').toLowerCase();
  return { provider, address: account, chainHex };
}

const PUBLIC = typeof process !== 'undefined' ? process.env.PUBLIC_URL || '' : '';

export const WALLET_TILES = [
  {
    id: 'metamask',
    label: 'MetaMask',
    icon: `${PUBLIC}/metamask-fox.svg`,
    provider: 'metamask',
    connection: 'injected',
    panel: 'metamask',
  },
  {
    id: 'phantom',
    label: 'Phantom',
    icon: `${PUBLIC}/phantom-mark.svg`,
    provider: 'phantom',
    connection: 'injected',
    panel: 'phantom',
  },
  {
    id: 'coinbase',
    label: 'Coinbase',
    icon: `${PUBLIC}/wallets/coinbase.svg`,
    provider: 'coinbase',
    connection: 'injected',
    panel: 'injected',
  },
  {
    id: 'brave',
    label: 'Brave',
    icon: `${PUBLIC}/wallets/brave.svg`,
    provider: 'brave',
    connection: 'injected',
    panel: 'injected',
  },
  {
    id: 'rabby',
    label: 'Rabby',
    icon: `${PUBLIC}/wallets/rabby.svg`,
    provider: 'rabby',
    connection: 'injected',
    panel: 'injected',
  },
  {
    id: 'okx',
    label: 'OKX',
    icon: `${PUBLIC}/wallets/okx.svg`,
    provider: 'okx',
    connection: 'injected',
    panel: 'injected',
  },
  {
    id: 'frame',
    label: 'Frame',
    icon: `${PUBLIC}/wallets/frame.svg`,
    provider: 'frame',
    connection: 'injected',
    panel: 'injected',
  },
  {
    id: 'trust',
    label: 'Trust',
    icon: `${PUBLIC}/wallets/trust.svg`,
    provider: 'trust',
    connection: 'walletconnect',
    panel: 'wc',
  },
  {
    id: 'rainbow',
    label: 'Rainbow',
    icon: `${PUBLIC}/wallets/rainbow.svg`,
    provider: 'rainbow',
    connection: 'walletconnect',
    panel: 'wc',
  },
  {
    id: 'bitget',
    label: 'Bitget',
    icon: `${PUBLIC}/wallets/bitget.svg`,
    provider: 'bitget',
    connection: 'walletconnect',
    panel: 'wc',
  },
  {
    id: 'tokenpocket',
    label: 'TokenPocket',
    icon: `${PUBLIC}/wallets/tokenpocket.svg`,
    provider: 'tokenpocket',
    connection: 'walletconnect',
    panel: 'wc',
  },
  {
    id: 'safe',
    label: 'Safe',
    icon: `${PUBLIC}/wallets/safe.svg`,
    provider: 'safe',
    connection: 'walletconnect',
    panel: 'wc',
  },
];

export function getWalletTile(id) {
  return WALLET_TILES.find((t) => t.id === id);
}
